// * Disable lint for logging
/* eslint-disable no-console */

import { CONSOLE_COLOR_CODE } from '@ricoh/ria.client.core/constants/core/console';
import { BaseError } from '@ricoh/ria.client.core/errors';
import DateTime from '@ricoh/ria.client.core/plugins/moment';
import { boolean } from '@ricoh/ria.client.core/utils/data/object';
import ansiRegex from 'ansi-regex';

import type { Logger, LogLevel } from './_api.type';

import { colorText, logLevelText } from './_styles';

const globalThisCore = globalThis as GlobalThisCore;

const CAPTURE_LIMIT = 100;
const ANSI_REGEX = ansiRegex();

/**
 * Check is logged.
 *
 * @param {string} flag Flag.
 * @param {LogLevel} level Log level.
 *
 * @returns {boolean} Is logged.
 */
function isLogged(flag: string, level: LogLevel): boolean {
  if (import.meta.env.PROD) {
    return false;
  }

  if (!boolean(import.meta.env.APP_LOGGER)) {
    return false;
  }

  if (JSON.parse<string[]>(import.meta.env.APP_LOGGER_IGNORE).some((ignore) => flag.startsWith(ignore))) {
    return false;
  }

  switch (import.meta.env.APP_LOGGER_LEVEL) {
    case 'error':
      return level === 'error';

    case 'warn':
      return level === 'error' || level === 'warn';

    case 'info':
      return level === 'error' || level === 'warn' || level === 'info';

    case 'log':
    default:
      return true;
  }
}

/**
 * Capture log.
 *
 * @param {string} message Log message.
 */
export function captureLog(message: string) {
  if (!globalThisCore.logger.shouldCapture) {
    return;
  }

  const logMessage = message.replace(ANSI_REGEX, '');

  const lasted = globalThisCore.logger.logs.at(0);
  if (lasted?.message === logMessage) {
    lasted.times += 1;

    return;
  }

  if (globalThisCore.logger.logs.length > CAPTURE_LIMIT) {
    globalThisCore.logger.logs.pop();
  }

  globalThisCore.logger.logs.unshift({
    now: DateTime().toJSON(),
    message: logMessage,
    times: 0,
  });
}

/**
 * Create logger.
 *
 * @param {string} flag Flag.
 *
 * @returns {Logger} Logger.
 */
export function createLogger(flag: string = 'global'): Logger {
  const flagTextFormat = colorText(` ${flag} `, CONSOLE_COLOR_CODE.WHITE, CONSOLE_COLOR_CODE.BG_LIGHT_BLUE);

  return {
    write: (level, message, { capture = true } = {}) => {
      const logMessage = `${logLevelText(level)} ${flagTextFormat} ${message}`;
      if (capture) {
        captureLog(logMessage);
      }

      if (isLogged(flag, level)) {
        console.groupCollapsed(logMessage);
        console.trace();
        console.groupEnd();
      }
    },

    writeWithData: (level, message, data, { capture = true } = {}) => {
      const logMessage = `${logLevelText(level)} ${flagTextFormat} ${message}`;
      if (capture) {
        captureLog(logMessage);
      }

      if (isLogged(flag, level)) {
        console.groupCollapsed(logMessage);

        globalThisCore.logger.shouldCapture = false;

        if (data instanceof BaseError) {
          console.error(data);
          console.error(`Error cause:\n%o`, data.cause);
        } else {
          if (level === 'log') {
            console.debug(data);
          } else {
            console.log(data);
          }

          if (level === 'error' || level === 'warn') {
            console.trace();
          }
        }

        globalThisCore.logger.shouldCapture = true;

        console.groupEnd();
      }
    },
  };
}
